import { Box } from "@vp/swan";
import React, { useEffect, useMemo, useState } from "react";
import usePricingContextInitialization from "../../hooks/usePricingContextInitialization";
import { usePricingDetails } from "../../hooks/usePricingDetails";
import { productPricingService } from "../../services/productPricingService";
import type { PriceProperties } from "../../types/pricing/price-display.types";
import { PriceDisplayType } from "../../types/pricing/price-display.types";
import { logger } from "../../util/logger";
import { isRangePriceDisplayGroup } from "../../util/price-calculator-helper";
import { calculateEvaluatedQuantity, calculateResolvedPrices } from "../../util/price-extraction-helper";
import { getPriceQueryId } from "../../util/price-queryId";
import PriceLoading from "../PriceLoading";
import { PriceDisplayV2Props } from "./PriceDisplayV2";
import { PriceDisplayWrapper } from "./PriceDisplayWrapper";

const VALID_PRICE_DISPLAY_TYPES = new Set<string>(Object.values(PriceDisplayType));

//Acts as a safeguard to not honor any display strategy present in PriceDisplay but not in PriceDisplay V2
const toValidDisplayStrategy = (value: string | undefined): PriceDisplayType | undefined => {
  if (value && VALID_PRICE_DISPLAY_TYPES.has(value)) {
    return value as PriceDisplayType;
  }
  return undefined;
};

export type PriceExtractionWrapperProps = PriceDisplayV2Props;

const PriceExtractionWrapper = ({
  mpvId,
  productId,
  version,
  selections,
  alternateSelections,
  requestedPriceDisplayStrategy,
  hideMinimumOrderableQuantity = false,
  displayVariant,
  alwaysShowMOQInfo = false,
  alignVAT,
  isTile,
  developmentMode,
}: PriceExtractionWrapperProps) => {
  const { pricingContextInitialized, vatIncl } = usePricingContextInitialization(
    productPricingService.tenant,
    productPricingService.locale,
    developmentMode,
  );
  const quantity = selections?.quantity ?? selections?.Quantity;

  const [lastValidQuantity, setLastValidQuantity] = useState<string | undefined>(quantity);
  const [lastValidPriceQueryId, setLastValidPriceQueryId] = useState<string | undefined>(undefined);

  const priceQueryId = getPriceQueryId({
    mpvId,
    productId,
    version,
    selections,
    alternateSelections,
    displayVariant,
  });

  // Get current and last valid price details
  const currentPriceDetails = usePricingDetails(priceQueryId);
  const lastValidPriceDetails = usePricingDetails(lastValidPriceQueryId ?? "");

  useEffect(() => {
    if (!pricingContextInitialized || !productPricingService.locale) return;

    productPricingService.queuePriceQuery({
      mpvId,
      productId,
      version,
      selections,
      alternateSelections,
      displayVariant,
      requestedPriceDisplayStrategy: toValidDisplayStrategy(requestedPriceDisplayStrategy),
    });
  }, [
    mpvId,
    productId,
    version,
    selections,
    alternateSelections,
    displayVariant,
    requestedPriceDisplayStrategy,
    pricingContextInitialized,
    productPricingService.tenant,
    productPricingService.locale,
  ]);

  // Update Cached Key/Quantity when we have valid price details
  useEffect(() => {
    const priceForCurrentQuantityExists =
      quantity &&
      currentPriceDetails?.prices?.estimatedPrices?.[
        Number(quantity)
      ];

    if (priceForCurrentQuantityExists) {
      setLastValidPriceQueryId(priceQueryId);
      setLastValidQuantity(quantity);
    }
  }, [currentPriceDetails, priceQueryId, quantity]);

  // Decide which productPriceDetails to use: current if available, else cached fallback
  const priceDetailsToUse = currentPriceDetails?.prices?.estimatedPrices
    ? currentPriceDetails
    : lastValidPriceDetails;

  const hasValidPriceData = priceDetailsToUse?.prices?.estimatedPrices;
  const evaluatedQuantity = calculateEvaluatedQuantity(priceDetailsToUse, quantity, lastValidQuantity);

  // Compute resolved prices based on display strategy and evaluated quantity
  const resolvedPrices = useMemo(() => {
    if (!hasValidPriceData) return null;
    return calculateResolvedPrices(priceDetailsToUse, evaluatedQuantity); ;
  }, [hasValidPriceData, priceDetailsToUse, evaluatedQuantity]);

  const isError = productPricingService.isError || priceDetailsToUse?.isError || currentPriceDetails?.isError;
  const isMissingResolvedPrices = !resolvedPrices && !isError && priceDetailsToUse;

  useEffect(() => {
    if (isError || isMissingResolvedPrices) {
      logger.warn("Price details are unavailable", {
        productId,
        version,
        mpvId,
        selections,
        quantity,
        currentPriceDetails,
        lastValidPriceDetails,
        isError,
        isMissingResolvedPrices,
      });
    }
  }, [isError, isMissingResolvedPrices]);

  if (isError || isMissingResolvedPrices) {
    return <></>;
  }

  // Render PriceDisplayWrapper when we have valid price data
  if (hasValidPriceData && resolvedPrices) {
    const priceProperties: PriceProperties = {
      minimumFractionDigits: priceDetailsToUse.prices?.fractionDigits ?? 2,
      currency: priceDetailsToUse.prices?.currency ?? "EUR",
      quantity: Number(evaluatedQuantity),
      vatInc: vatIncl,
      hideSetupFee: !isRangePriceDisplayGroup(priceDetailsToUse?.priceDisplayGroup),
      doesOfferExist: priceDetailsToUse.prices?.estimatedPrices[Number(evaluatedQuantity)]?.discountMetaData?.doesOfferExist,
      alignVAT,
      isTile,
      hideMinimumOrderableQuantity,
      alwaysShowMOQInfo,
    };
    return (
      <Box aria-live="polite" aria-atomic="true" data-testid="price-extraction-wrapper-test-id">
        <PriceDisplayWrapper
          priceDisplayType={priceDetailsToUse.displayStrategy}
          prices={resolvedPrices}
          priceProperties={priceProperties}
        />
      </Box>
    );
  }

  return (
    <PriceLoading />
  );
};

export { PriceExtractionWrapper };
